import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronDown, TrendingUp, Edit, Calendar, Layers } from 'lucide-react';
import { theme } from '../../../theme';
import { useOwnerDashboard } from '../../../hooks/venue/useOwnerDashboard';
import ShareLinkButton from '../../../components/venue/owner/ShareLinkButton';

interface VenueItem {
  id: string;
  name: string;
  complexName: string;
  type: string;
  surface: string;
  status: 'ACTIVE' | 'PAUSED';
  monthlyBookings: number;
  monthlyRevenue: number;
  rating: number;
  thumbnailUrl?: string;
}

const ManageVenuesPageEnhanced: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterComplex, setFilterComplex] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const { venues: rawVenues, loading, error, fetchVenues } = useOwnerDashboard();

  useEffect(() => {
    fetchVenues();
  }, [fetchVenues]);

  // Transform API data to component interface
  const venues: VenueItem[] = rawVenues.map(v => ({
    id: v.id,
    name: v.name,
    complexName: v.complex?.name || v.name,
    type: v.venueType,
    surface: v.surfaceType || 'Césped sintético',
    status: v.isActive ? 'ACTIVE' : 'PAUSED',
    monthlyBookings: v.stats?.totalBookings || 0,
    monthlyRevenue: v.stats?.totalRevenue || 0,
    rating: v.averageRating || 0,
    thumbnailUrl: v.photos?.[0] || undefined,
  }));

  // Apply filters
  const filteredVenues = venues.filter(venue => {
    const matchesSearch = venue.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' ||
                          (filterStatus === 'active' && venue.status === 'ACTIVE') ||
                          (filterStatus === 'paused' && venue.status === 'PAUSED');
    const matchesType = filterType === 'all' || venue.type === filterType;

    return matchesSearch && matchesStatus && matchesType;
  });

  // Styles
  const containerStyle: React.CSSProperties = {
    backgroundColor: theme.colors.background,
    minHeight: '100vh',
    paddingBottom: '48px',
  };

  const headerStyle: React.CSSProperties = {
    padding: '32px 48px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  };

  const titleRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const titleSectionStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: theme.fonts.heading,
    fontSize: '48px',
    color: theme.colors.text,
    margin: 0,
    letterSpacing: '2px',
  };

  const subtitleStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '14px',
    color: theme.colors.textSecondary,
    margin: 0,
  };

  const createBtnStyle: React.CSSProperties = {
    padding: '16px 32px',
    borderRadius: '12px',
    backgroundColor: theme.colors.primary,
    border: 'none',
    fontFamily: theme.fonts.body,
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#000',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  };

  const filtersBarStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: '12px',
    border: `1px solid ${theme.colors.border}`,
    padding: '16px',
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  };

  const searchBoxStyle: React.CSSProperties = {
    width: '320px',
    height: '40px',
    borderRadius: '8px',
    backgroundColor: theme.colors.background,
    border: `1px solid ${theme.colors.border}`,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '0 12px',
  };

  const searchInputStyle: React.CSSProperties = {
    flex: 1,
    backgroundColor: 'transparent',
    border: 'none',
    fontFamily: theme.fonts.body,
    fontSize: '13px',
    color: theme.colors.text,
    outline: 'none',
  };

  const filterStyle: React.CSSProperties = {
    height: '40px',
    borderRadius: '8px',
    backgroundColor: theme.colors.background,
    border: `1px solid ${theme.colors.border}`,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '0 16px',
    cursor: 'pointer',
  };

  const filterLabelStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '12px',
    fontWeight: '600',
    color: theme.colors.textSecondary,
  };

  const filterValueStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '12px',
    color: theme.colors.text,
  };

  const venuesGridStyle: React.CSSProperties = {
    padding: '0 48px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  };

  const venueCardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: '18px',
    border: `1px solid ${theme.colors.border}`,
    padding: '24px',
    display: 'flex',
    gap: '20px',
  };

  const thumbnailStyle: React.CSSProperties = {
    width: '120px',
    height: '120px',
    borderRadius: '12px',
    backgroundColor: '#1A1A1A',
    flexShrink: 0,
  };

  const venueContentStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  };

  const venueHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  };

  const venueInfoStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  };

  const venueNameStyle: React.CSSProperties = {
    fontFamily: theme.fonts.heading,
    fontSize: '22px',
    color: theme.colors.text,
    margin: 0,
    letterSpacing: '1px',
  };

  const venueMetaStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  };

  const metaItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  };

  const metaTextStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '12px',
    color: theme.colors.textSecondary,
  };

  const badgeStyle = (status: string): React.CSSProperties => ({
    backgroundColor: status === 'ACTIVE' ? theme.colors.success : '#33383B',
    color: status === 'ACTIVE' ? '#000' : theme.colors.textSecondary,
    borderRadius: '20px',
    padding: '6px 16px',
    fontFamily: theme.fonts.body,
    fontSize: '12px',
    fontWeight: '600',
  });

  const statsRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '24px',
  };

  const statStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  };

  const statLabelStyle: React.CSSProperties = {
    fontFamily: theme.fonts.body,
    fontSize: '11px',
    color: theme.colors.textSecondary,
  };

  const statValueStyle: React.CSSProperties = {
    fontFamily: theme.fonts.heading,
    fontSize: '20px',
    color: theme.colors.text,
  };

  const actionsRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
  };

  const actionBtnStyle = (variant: 'primary' | 'secondary' | 'warning'): React.CSSProperties => {
    const colors = {
      primary: { bg: 'transparent', border: theme.colors.primary, text: theme.colors.primary },
      secondary: { bg: 'transparent', border: theme.colors.border, text: theme.colors.textSecondary },
      warning: { bg: 'transparent', border: theme.colors.warning, text: theme.colors.warning },
    };

    const style = colors[variant];
    return {
      padding: '0 24px',
      height: '44px',
      borderRadius: '12px',
      backgroundColor: style.bg,
      border: `2px solid ${style.border}`,
      fontFamily: theme.fonts.body,
      fontSize: '14px',
      fontWeight: '600',
      color: style.text,
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    };
  };

  if (loading) {
    return (
      <div style={{ ...containerStyle, padding: '48px', textAlign: 'center' }}>
        Cargando canchas...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        ...containerStyle,
        padding: '48px',
        textAlign: 'center',
        color: theme.colors.danger
      }}>
        {error}
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={titleRowStyle}>
          <div style={titleSectionStyle}>
            <h1 style={titleStyle}>MIS CANCHAS</h1>
            <p style={subtitleStyle}>Gestiona todas tus canchas desde aquí</p>
          </div>
          <button
            style={createBtnStyle}
            onClick={() => navigate('/owner/venues/create')}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          >
            Crear Nueva Cancha
          </button>
        </div>

        {/* Filters Bar */}
        <div style={filtersBarStyle}>
          {/* Search */}
          <div style={searchBoxStyle}>
            <Search size={16} color={theme.colors.textSecondary} />
            <input
              type="text"
              placeholder="Buscar por nombre..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={searchInputStyle}
            />
          </div>

          {/* Filter: Complex */}
          <div style={filterStyle}>
            <span style={filterLabelStyle}>Complejo:</span>
            <span style={filterValueStyle}>Todos</span>
            <ChevronDown size={14} color={theme.colors.textSecondary} />
          </div>

          {/* Filter: Status */}
          <div style={filterStyle}>
            <span style={filterLabelStyle}>Estado:</span>
            <span style={filterValueStyle}>Todas</span>
            <ChevronDown size={14} color={theme.colors.textSecondary} />
          </div>

          {/* Filter: Type */}
          <div style={filterStyle}>
            <span style={filterLabelStyle}>Tipo:</span>
            <span style={filterValueStyle}>Todas</span>
            <ChevronDown size={14} color={theme.colors.textSecondary} />
          </div>
        </div>
      </div>

      {/* Venues Grid */}
      <div style={venuesGridStyle}>
        {filteredVenues.map((venue) => (
          <div key={venue.id} style={venueCardStyle}>
            {/* Thumbnail */}
            <div style={thumbnailStyle} />

            {/* Content */}
            <div style={venueContentStyle}>
              {/* Header */}
              <div style={venueHeaderStyle}>
                <div style={venueInfoStyle}>
                  <h3 style={venueNameStyle}>{venue.name}</h3>
                  <div style={venueMetaStyle}>
                    <div style={metaItemStyle}>
                      <span style={{ fontSize: '14px' }}>⚽</span>
                      <span style={metaTextStyle}>{venue.type}</span>
                    </div>
                    <div style={metaItemStyle}>
                      <Layers size={14} color={theme.colors.textSecondary} />
                      <span style={metaTextStyle}>{venue.surface}</span>
                    </div>
                  </div>
                </div>
                <span style={badgeStyle(venue.status)}>
                  {venue.status === 'ACTIVE' ? 'Activa' : 'Pausada'}
                </span>
              </div>

              {/* Stats */}
              <div style={statsRowStyle}>
                <div style={statStyle}>
                  <span style={statLabelStyle}>Reservas del mes</span>
                  <span
                    style={{
                      ...statValueStyle,
                      color: venue.status === 'PAUSED' ? theme.colors.textSecondary : theme.colors.text,
                      opacity: venue.status === 'PAUSED' ? 0.6 : 1,
                    }}
                  >
                    {venue.monthlyBookings}
                  </span>
                </div>
                <div style={statStyle}>
                  <span style={statLabelStyle}>Revenue del mes</span>
                  <span
                    style={{
                      ...statValueStyle,
                      color: venue.status === 'PAUSED' ? theme.colors.textSecondary : theme.colors.text,
                      opacity: venue.status === 'PAUSED' ? 0.6 : 1,
                    }}
                  >
                    ${venue.monthlyRevenue.toLocaleString()}
                  </span>
                </div>
                <div style={statStyle}>
                  <span style={statLabelStyle}>Rating</span>
                  <span style={statValueStyle}>{venue.rating} ⭐</span>
                </div>
              </div>

              {/* Actions */}
              <div style={actionsRowStyle}>
                <button style={actionBtnStyle('secondary')}>Ver Detalle</button>
                <button style={actionBtnStyle('secondary')}>Editar</button>
                <button style={actionBtnStyle('secondary')}>Config Horarios</button>
                <ShareLinkButton venueId={venue.id} venueName={venue.name} />
                {venue.status === 'ACTIVE' ? (
                  <button style={actionBtnStyle('warning')}>Pausar</button>
                ) : (
                  <button
                    style={{
                      ...actionBtnStyle('primary'),
                      backgroundColor: theme.colors.success,
                      borderColor: theme.colors.success,
                      color: '#000',
                    }}
                  >
                    Activar
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ManageVenuesPageEnhanced;
